import type { TChatMessageFile, TChatThreadHistoryMessage } from "@/api/chat/chatThreads.types";
import type { TMessage, TMessageFile } from "@/types/chat";
import {
  handleWebSocketAuthError,
  handleWebSocketCloseAuthError,
  mapWebSocketCloseCodeToMessage,
} from "@/utils/websocketUtils";
import { useCallback, useEffect, useRef, useState } from "react";

import { chatThreadsService } from "@/api/chat";
import { useStore } from "@/store";
import { useChatStore } from "@/store/chat/chat.store";
import type { TChatThreadContext } from "@/store/chat/createChatSlice";
import { apiURL } from "@/utils/env";
import { sanitizeUpstreamErrorMessage } from "@/utils/sanitizeUpstreamErrorMessage";
import { tokenStorage } from "@/utils/tokenStorage";

const AI_SYSTEM_USER_ID = "00000000-0000-0000-0000-000000000000";

type TWebSocketThreadHistoryMessage = {
  type: "thread_history";
  thread_id?: string;
  messages: TChatThreadHistoryMessage[];
};

type TWebSocketUserMessage = {
  type: "user_message";
  user_id?: string | null;
  message_id?: string;
  content?: string;
  timestamp?: string;
  files?: TChatMessageFile[];
  attachments?: { file_id: string; filename?: string; url?: string }[];
  message_type?: "user" | "ai";
};

type TWebSocketAIMessageStream = {
  type: "ai_message_stream";
  message_id: string;
  token: string;
};

type TWebSocketAIMessageComplete = {
  type: "ai_message_complete";
  message_id: string;
};

type TWebSocketAIMessage = {
  type: "ai_message";
  thread_id: string;
  content: string;
  message_id: string;
  message_type: "ai";
  timestamp?: string;
};

type TWebSocketAIStatus = {
  type: "ai_status";
  thread_id: string;
  status: "thinking" | "typing" | "complete" | "error";
  message: string;
  message_id?: string | null;
  timestamp?: string;
};

type TWebSocketErrorMessage = {
  type: "error";
  code?: string;
  message: string;
  error_code?: string;
  details?: {
    thread_id?: string;
    user_message_id?: string;
  };
};

type TWebSocketMessage =
  | TWebSocketThreadHistoryMessage
  | TWebSocketUserMessage
  | TWebSocketAIMessageStream
  | TWebSocketAIMessageComplete
  | TWebSocketAIMessage
  | TWebSocketAIStatus
  | TWebSocketErrorMessage;

const createMessageId = () =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `msg-${Date.now()}-${Math.random()}`;

/** Map attachments to TMessage.files format. Uses attachments as single source of truth. */
const attachmentsToFiles = (attachments: unknown): TMessageFile[] | undefined => {
  if (!attachments || !Array.isArray(attachments)) return undefined;
  const arr = attachments as { file_id: string; filename?: string; url?: string }[];
  if (arr.length === 0) return undefined;
  return arr.map((a) => {
    const url = a.url?.startsWith("http")
      ? a.url
      : a.url
        ? apiURL(a.url).toString()
        : apiURL(`files/${a.file_id}/download`).toString();
    return {
      id: a.file_id,
      name: a.filename ?? "file",
      url,
    };
  });
};

const extractAIResponse = (content: string | undefined) => {
  if (!content) {
    return null;
  }

  const marker = "ai response:";
  const lower = content.toLowerCase();
  const index = lower.indexOf(marker);

  if (index === -1) {
    return null;
  }

  const responseText = content.slice(index + marker.length).trim();

  return responseText;
};

type TChatThreadSessionState = {
  status: "idle" | "connecting" | "connected" | "error";
  messages: TMessage[];
  error: string | null;
};

type TSendMessageOptions = {
  skipOptimistic?: boolean;
};

export const useChatThreadSession = (context: TChatThreadContext | null) => {
  // No thread = no WebSocket connection. Return empty state for new chats.
  // The new chat UI is handled by SectionNewChatPage which doesn't need WebSocket.
  const hasThreadId = Boolean(context?.threadId);

  // Initialize state - if context has no threadId, start with empty messages
  const [state, setState] = useState<TChatThreadSessionState>(() => {
    return {
      status: hasThreadId ? "idle" : "idle",
      messages: [],
      error: null,
    };
  });
  const currentUserId = useStore.use.user()?.id ?? null;

  // Get store actions for caching and clearing initial prompt
  const setCachedThreadMessages = useChatStore((s) => s.setCachedThreadMessages);
  const getCachedThreadMessages = useChatStore((s) => s.getCachedThreadMessages);
  const clearThreadInitialPrompt = useChatStore((s) => s.clearThreadInitialPrompt);
  const websocketRef = useRef<WebSocket | null>(null);
  const pendingPromptRef = useRef<string | null>(context?.initialPrompt ?? null);
  const pendingFileIdsRef = useRef<string[]>(context?.initialFileIds ?? []);
  const optimisticQueueRef = useRef<string[]>([]);
  const thinkingMessageIdRef = useRef<string | null>(null);
  const previousThreadIdRef = useRef<string | null>(context?.threadId ?? null);
  const currentThreadIdRef = useRef<string | null>(context?.threadId ?? null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const reconnectAttemptsRef = useRef<number>(0);
  const isIntentionalCloseRef = useRef<boolean>(false);
  const thinkingTimeoutRef = useRef<number | null>(null);
  const MAX_RECONNECT_ATTEMPTS = 10;
  const THINKING_TIMEOUT_MS = 60_000;

  const resolveSender = (senderId: string | undefined | null, messageType?: "user" | "ai" | null): "user" | "ai" => {
    // Prefer message_type if available (more reliable)
    if (messageType) {
      return messageType;
    }
    // Fall back to sender_id logic
    return !senderId || senderId === AI_SYSTEM_USER_ID ? "ai" : "user";
  };

  const extractSenderId = (payload: { sender_id?: string | null; user_id?: string | null }): string | null => {
    return payload.sender_id ?? payload.user_id ?? null;
  };

  useEffect(() => {
    pendingPromptRef.current = context?.initialPrompt ?? null;
    pendingFileIdsRef.current = context?.initialFileIds ?? [];
  }, [context?.initialPrompt, context?.initialFileIds]);

  // Keep currentThreadIdRef in sync with context for real-time access in WebSocket handlers
  useEffect(() => {
    currentThreadIdRef.current = context?.threadId ?? null;
  }, [context?.threadId]);

  const seenMessageIdsRef = useRef<Set<string>>(new Set());

  const updateMessages = useCallback((updater: (messages: TMessage[]) => TMessage[]) => {
    setState((prev) => ({
      ...prev,
      messages: updater(prev.messages),
    }));
  }, []);

  const addThinkingMessage = useCallback(() => {
    if (thinkingMessageIdRef.current) {
      return;
    }
    const placeholderId = `thinking-${Date.now()}`;
    thinkingMessageIdRef.current = placeholderId;

    // Auto-expire thinking bubble if backend never responds
    if (thinkingTimeoutRef.current) clearTimeout(thinkingTimeoutRef.current);
    thinkingTimeoutRef.current = window.setTimeout(() => {
      if (!thinkingMessageIdRef.current) return;
      const expiredId = thinkingMessageIdRef.current;
      thinkingMessageIdRef.current = null;
      updateMessages((prev) =>
        prev.map((msg) =>
          msg.id === expiredId
            ? { ...msg, thinking: false, streaming: false, text: "", error: "Something went wrong. Please try again." }
            : msg
        )
      );
    }, THINKING_TIMEOUT_MS);

    updateMessages((prev) => [
      ...prev,
      {
        id: placeholderId,
        text: "",
        sender: "ai",
        streaming: true,
        thinking: true,
      },
    ]);
  }, [updateMessages]);

  const removeThinkingMessage = useCallback(() => {
    if (thinkingTimeoutRef.current) {
      clearTimeout(thinkingTimeoutRef.current);
      thinkingTimeoutRef.current = null;
    }
    if (!thinkingMessageIdRef.current) {
      return;
    }
    const placeholderId = thinkingMessageIdRef.current;
    thinkingMessageIdRef.current = null;
    updateMessages((prev) => prev.filter((message) => message.id !== placeholderId));
  }, [updateMessages]);

  const addOptimisticMessage = useCallback(
    (content: string, files?: TMessageFile[]) => {
      const tempId = `temp-${Date.now()}`;
      optimisticQueueRef.current.push(tempId);
      updateMessages((prev) => [
        ...prev,
        {
          id: tempId,
          text: content,
          sender: "user",
          optimistic: true,
          files: files && files.length > 0 ? files : undefined,
          createdAt: new Date().toISOString(),
        },
      ]);
    },
    [updateMessages]
  );

  const resolveOptimisticMessage = useCallback(
    (incoming: TMessage) => {
      if (incoming.id) {
        seenMessageIdsRef.current.add(incoming.id);
      }
      const optimisticId = optimisticQueueRef.current.shift();
      updateMessages((prev) => {
        const existingIndex = prev.findIndex((message) => message.id === incoming.id);
        if (existingIndex !== -1) {
          const next = [...prev];
          next[existingIndex] = { ...incoming, optimistic: false };
          return next;
        }

        if (!optimisticId) {
          return [...prev, incoming];
        }

        const index = prev.findIndex((message) => message.id === optimisticId);
        if (index === -1) {
          return [...prev, incoming];
        }
        const next = [...prev];
        next[index] = { ...incoming, optimistic: false };
        return next;
      });
    },
    [updateMessages]
  );

  const appendMessage = useCallback(
    (message: TMessage) => {
      updateMessages((prev) => {
        if (message.id && prev.some((existing) => existing.id === message.id)) {
          return prev;
        }
        if (message.id) {
          seenMessageIdsRef.current.add(message.id);
        }
        const lastMessage = prev[prev.length - 1];
        if (
          message.sender === "user" &&
          lastMessage?.sender === "user" &&
          (lastMessage.id === message.id || lastMessage.text === message.text)
        ) {
          return prev;
        }
        return [...prev, message];
      });
    },
    [updateMessages]
  );

  const upsertStreamingMessage = useCallback(
    (messageId: string, token: string, isComplete = false) => {
      updateMessages((prev) => {
        const index = prev.findIndex((message) => message.id === messageId);
        if (index === -1) {
          return [
            ...prev,
            {
              id: messageId,
              text: token,
              sender: "ai",
              streaming: !isComplete,
            },
          ];
        }
        const next = [...prev];
        const existing = next[index];
        next[index] = {
          ...existing,
          text: `${existing.text ?? ""}${token ?? ""}`,
          streaming: !isComplete,
        };
        return next;
      });
    },
    [updateMessages]
  );

  const setErrorState = useCallback((message: string) => {
    setState((prev) => ({
      ...prev,
      status: "error",
      error: message,
    }));
  }, []);

  useEffect(() => {
    const threadId = context?.threadId ?? null;
    const prevThreadId = previousThreadIdRef.current;

    // No thread = no WebSocket. Early return for new chats.
    // The /new route handles new chat creation without WebSocket.
    if (!threadId) {
      // Close any existing websocket connection
      if (websocketRef.current) {
        const oldWebsocket = websocketRef.current;
        websocketRef.current = null;
        isIntentionalCloseRef.current = true;
        oldWebsocket.close(1000, "No thread");
      }
      // Cancel any pending reconnection attempts
      if (reconnectTimeoutRef.current !== null) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      previousThreadIdRef.current = null;
      return;
    }

    // Clear all state when threadId changes (switching from one thread to another)
    if (prevThreadId !== threadId) {
      // Cancel any pending reconnection attempts
      if (reconnectTimeoutRef.current !== null) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (thinkingTimeoutRef.current !== null) {
        clearTimeout(thinkingTimeoutRef.current);
        thinkingTimeoutRef.current = null;
      }
      reconnectAttemptsRef.current = 0;
      isIntentionalCloseRef.current = true;

      optimisticQueueRef.current = [];
      seenMessageIdsRef.current = new Set();
      thinkingMessageIdRef.current = null;
      previousThreadIdRef.current = threadId;

      // Load cached messages immediately for instant display (hybrid caching)
      // Fresh data will replace this when WebSocket sends thread_history
      const cachedMessages = getCachedThreadMessages(threadId);

      setState({
        status: "idle",
        messages: cachedMessages,
        error: null,
      });

      // Close any existing websocket connection
      // Set ref to null FIRST so in-flight messages are ignored by the stale WebSocket check
      if (websocketRef.current) {
        const oldWebsocket = websocketRef.current;
        websocketRef.current = null;
        oldWebsocket.close(1000, "Thread switched");
      }
    }

    const token = tokenStorage.getAccessToken();
    if (!token) {
      setErrorState("Authentication required. Please sign in again.");
      return;
    }

    // Reset reconnection state for new connection
    reconnectAttemptsRef.current = 0;
    isIntentionalCloseRef.current = false;

    // Clear messages and set connecting status
    setState({
      status: "connecting",
      messages: [],
      error: null,
    });

    const connectWebSocket = () => {
      const websocketURL = chatThreadsService.getWebSocketURL(threadId, token);
      const websocket = new WebSocket(websocketURL);
      websocketRef.current = websocket;

      websocket.onopen = () => {
        // Reset reconnection attempts on successful connection
        reconnectAttemptsRef.current = 0;
        setState((prev) => ({
          ...prev,
          status: "connected",
          error: null,
        }));

        if (pendingPromptRef.current != null || (pendingFileIdsRef.current && pendingFileIdsRef.current.length > 0)) {
          const initialPrompt = pendingPromptRef.current ?? "";
          const initialFileIds = pendingFileIdsRef.current ?? [];
          pendingPromptRef.current = null;
          pendingFileIdsRef.current = [];
          const filesForDisplay: TMessageFile[] =
            initialFileIds.length > 0
              ? initialFileIds.map((id) => ({ id, name: "Uploaded file", url: undefined }))
              : [];
          addOptimisticMessage(initialPrompt || "(Attachment)", filesForDisplay);
          // Don't call addThinkingMessage() - let ai_status handler manage thinking status
          websocket.send(
            JSON.stringify({
              type: "user_message",
              content: initialPrompt,
              ...(initialFileIds.length > 0 ? { file_ids: initialFileIds } : {}),
            })
          );
          // Clear initialPrompt from store so it isn't re-sent on remount or effect re-run
          clearThreadInitialPrompt();
        }
      };

      websocket.onerror = () => {
        // Error will be handled by onclose
        console.error("[chat] WebSocket error");
      };

      websocket.onclose = (event) => {
        websocketRef.current = null;

        // Don't reconnect if this was an intentional close (thread switch, component unmount)
        if (isIntentionalCloseRef.current) {
          return;
        }

        // Check for authentication failure (code 1008 with "Authentication failed" reason)
        // This must be checked before the generic 1008 check which handles "Access denied"
        if (handleWebSocketCloseAuthError(event.code, event.reason)) {
          return; // Logout is handled, don't attempt reconnection
        }

        // Don't reconnect on access denied (1008)
        if (event.code === 1008) {
          setErrorState(mapWebSocketCloseCodeToMessage(1008, event.reason));
          return;
        }

        // Don't reconnect on normal closure (1000) unless it's unexpected
        if (event.code === 1000) {
          // Normal closure - only update status if still connected
          setState((prev) => {
            if (prev.status === "connected") {
              return { ...prev, status: "idle" };
            }
            return prev;
          });
          return;
        }

        // Attempt reconnection for unexpected disconnects
        if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
          setErrorState("Failed to reconnect after multiple attempts. Please refresh the page.");
          return;
        }

        // Calculate delay with exponential backoff (max 30 seconds)
        const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
        reconnectAttemptsRef.current += 1;

        setState((prev) => ({
          ...prev,
          status: "connecting",
          error: `Reconnecting... (attempt ${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})`,
        }));

        const currentThreadId = threadId; // Capture threadId for reconnection check
        reconnectTimeoutRef.current = window.setTimeout(() => {
          // Only reconnect if threadId hasn't changed and we still need to connect
          if (previousThreadIdRef.current === currentThreadId && !isIntentionalCloseRef.current) {
            connectWebSocket();
          }
        }, delay);
      };

      websocket.onmessage = (rawEvent) => {
        // Ignore messages from WebSockets that are no longer the active connection
        // This prevents stale messages from appearing when switching threads or starting new chats
        if (websocketRef.current !== websocket) {
          console.info("[chat] Ignoring message from stale WebSocket connection");
          return;
        }

        const rawData: unknown = rawEvent.data;
        console.info("[chat] Received raw WebSocket message", { data: rawData });
        try {
          const data = JSON.parse(rawEvent.data as string) as TWebSocketMessage;
          switch (data.type) {
            case "thread_history": {
              // Ignore history for threads that don't match current context
              // This prevents old thread messages from appearing when switching to new chat
              const incomingThreadId = data.thread_id;
              const currentContextThreadId = currentThreadIdRef.current;

              if (incomingThreadId && currentContextThreadId !== incomingThreadId) {
                console.info("[chat] Ignoring thread_history for mismatched thread", {
                  incoming: incomingThreadId,
                  current: currentContextThreadId,
                });
                break;
              }

              // Replace all messages with thread history - this is the authoritative source
              const historyMessages: TMessage[] = Array.isArray(data.messages)
                ? data.messages.map((message: TChatThreadHistoryMessage) => {
                    const senderId = extractSenderId(message as { sender_id?: string | null; user_id?: string | null });
                    const sender = resolveSender(senderId, message.message_type);
                    const aiResponseText = sender === "ai" ? extractAIResponse(message.content) : null;
                    return {
                      id: message.id,
                      text: aiResponseText ?? message.content ?? "",
                      sender,
                      messageType: message.message_type,
                      helperText: aiResponseText ? message.content : undefined,
                      files: attachmentsToFiles(message.attachments),
                      createdAt: message.created_at ?? null,
                    };
                  })
                : [];

              // Preserve optimistic messages if they were added in onopen before thread_history arrived
              const optimisticIds = [...optimisticQueueRef.current];
              const hasOptimistic = optimisticIds.length > 0;

              // Clear optimistic queue and seen IDs only when NOT preserving (full replace)
              if (!hasOptimistic) {
                optimisticQueueRef.current = [];
              }
              seenMessageIdsRef.current = new Set(historyMessages.map((message) => message.id).filter(Boolean));
              thinkingMessageIdRef.current = null;

              // Check if this is a new thread (no AI messages in history)
              const hasAiMessages = historyMessages.some((msg) => msg.sender === "ai");
              const hasUserMessages = historyMessages.some((msg) => msg.sender === "user");
              const isNewThread = !hasAiMessages && hasUserMessages;

              // Replace messages: merge history with preserved optimistic messages (user message first, then thinking).
              // An optimistic message whose content already appears in history was persisted before the
              // reconnect — appending it again would duplicate the bubble, so it is dropped and dequeued.
              setState((prev) => {
                const historyUserTexts = new Set(
                  historyMessages.filter((m) => m.sender === "user").map((m) => m.text)
                );
                const preservedOptimistic = hasOptimistic
                  ? prev.messages.filter(
                      (m) =>
                        optimisticIds.includes(m.id) && !(m.sender === "user" && historyUserTexts.has(m.text))
                    )
                  : [];
                optimisticQueueRef.current = preservedOptimistic.map((m) => m.id);
                const merged = [...historyMessages, ...preservedOptimistic];
                if (threadId) {
                  setCachedThreadMessages(threadId, merged);
                }
                return { ...prev, messages: merged };
              });

              // Only add thinking when the merged list already has a user message (from history or optimistic)
              // Do NOT add when we have pendingPromptRef but no user in list yet - onopen/ai_status will add it
              const mergedHasUserMessage = hasUserMessages || hasOptimistic;
              if ((isNewThread || (mergedHasUserMessage && !hasAiMessages)) && mergedHasUserMessage) {
                // If the last user message is older than 5 minutes, it's a stale unanswered thread —
                // mark it failed instead of silently retriggering the AI.
                const lastUserMessage = [...historyMessages].reverse().find((m) => m.sender === "user");
                const isStale =
                  lastUserMessage?.createdAt != null &&
                  Date.now() - new Date(lastUserMessage.createdAt).getTime() > 5 * 60 * 1000;

                if (isStale) {
                  setState((prev) => ({
                    ...prev,
                    messages: prev.messages.map((m) =>
                      m.id === lastUserMessage?.id
                        ? { ...m, error: "This message didn't get a response. Try resending." }
                        : m
                    ),
                  }));
                } else {
                  addThinkingMessage();
                }
              }
              break;
            }
            case "user_message": {
              const userMessageData = data;
              const senderId = extractSenderId(
                userMessageData as { sender_id?: string | null; user_id?: string | null }
              );
              const sender: "user" | "ai" = resolveSender(senderId, userMessageData.message_type);

              const createMessagePayload = (content: string | undefined): TMessage => {
                const files = attachmentsToFiles(userMessageData.attachments);

                return {
                  id: userMessageData.message_id ?? createMessageId(),
                  text: content ?? "",
                  sender,
                  messageType: userMessageData.message_type ?? (sender === "ai" ? "ai" : "user"),
                  files,
                  createdAt: userMessageData.timestamp ?? new Date().toISOString(),
                };
              };

              let messageToAppend: TMessage | null = createMessagePayload(userMessageData.content);

              if (sender === "ai") {
                const parsed = extractAIResponse(data.content);
                if (parsed) {
                  messageToAppend = {
                    ...messageToAppend,
                    text: parsed,
                  };
                }
              }

              if (!messageToAppend) {
                break;
              }

              const isCurrentUserMessage = sender === "user" && senderId && currentUserId && senderId === currentUserId;
              const hasPendingOptimistic = sender === "user" && optimisticQueueRef.current.length > 0;

              if (sender === "user" && (isCurrentUserMessage || hasPendingOptimistic)) {
                resolveOptimisticMessage(messageToAppend);
                // Don't remove thinking message here - keep it until AI responds
              } else {
                if (sender === "ai") {
                  // Only remove thinking message when AI actually responds
                  removeThinkingMessage();
                  // Also remove any status indicators when AI message arrives
                  if (threadId) {
                    const statusMessageId = `ai-status-${threadId}`;
                    updateMessages((prev: TMessage[]) => prev.filter((msg: TMessage) => msg.id !== statusMessageId));
                  }
                }
                if (messageToAppend.id) {
                  if (seenMessageIdsRef.current.has(messageToAppend.id)) {
                    break;
                  }
                  seenMessageIdsRef.current.add(messageToAppend.id);
                }
                appendMessage(messageToAppend);
              }
              break;
            }
            case "ai_message_stream": {
              // Don't remove thinking message here - keep it until complete
              // Only remove status indicator, but keep thinking message
              if (threadId) {
                const statusMessageId = `ai-status-${threadId}`;
                updateMessages((prev: TMessage[]) => prev.filter((msg: TMessage) => msg.id !== statusMessageId));
              }
              upsertStreamingMessage(data.message_id, data.token);
              break;
            }
            case "ai_message_complete": {
              // Remove thinking message and status indicators when streaming completes
              removeThinkingMessage();
              if (threadId) {
                const statusMessageId = `ai-status-${threadId}`;
                updateMessages((prev: TMessage[]) => prev.filter((msg: TMessage) => msg.id !== statusMessageId));
              }
              upsertStreamingMessage(data.message_id, "", true);
              break;
            }
            case "ai_status": {
              const statusData = data;
              const statusMessageId = `ai-status-${statusData.thread_id}`;

              if (statusData.status === "thinking" || statusData.status === "typing") {
                // Replace placeholder thinking (from thread_history) with ai_status - single source of truth
                const placeholderId = thinkingMessageIdRef.current;
                if (placeholderId) {
                  thinkingMessageIdRef.current = null;
                }
                // Create or update status indicator - insert after last user message (not at end)
                // This ensures thinking appears after the user's message, including on retry
                updateMessages((prev: TMessage[]) => {
                  const withoutStatus = prev.filter((msg) => msg.id !== statusMessageId && msg.id !== placeholderId);
                  const statusMessage: TMessage = {
                    id: statusMessageId,
                    text: statusData.message || "Thinking",
                    sender: "ai",
                    status: statusData.status,
                    thinking: statusData.status === "thinking",
                    streaming: statusData.status === "typing",
                  };
                  let lastUserIndex = -1;
                  for (let i = withoutStatus.length - 1; i >= 0; i--) {
                    if (withoutStatus[i]?.sender === "user") {
                      lastUserIndex = i;
                      break;
                    }
                  }
                  const insertIndex = lastUserIndex === -1 ? withoutStatus.length : lastUserIndex + 1;
                  return [...withoutStatus.slice(0, insertIndex), statusMessage, ...withoutStatus.slice(insertIndex)];
                });
              } else if (statusData.status === "complete" || statusData.status === "error") {
                // Only remove status indicator when we get complete or error
                updateMessages((prev: TMessage[]) => prev.filter((msg: TMessage) => msg.id !== statusMessageId));
                removeThinkingMessage();

                if (statusData.status === "error") {
                  // Error handling is done in error case handler
                  // Don't set error in state here if it's associated with a user message
                }
              }
              break;
            }
            case "ai_message": {
              const aiMessageData = data;
              // Remove thinking message and status indicators when AI message arrives
              removeThinkingMessage();

              // Remove any status indicators
              const statusMessageId = `ai-status-${aiMessageData.thread_id}`;
              updateMessages((prev) => {
                const filtered = prev.filter((msg) => msg.id !== statusMessageId);

                // Extract AI response text
                const aiResponseText = extractAIResponse(aiMessageData.content);
                const messageText = aiResponseText ?? aiMessageData.content ?? "";

                // Check if message already exists
                const existingIndex = filtered.findIndex((msg) => msg.id === aiMessageData.message_id);

                const aiMessage: TMessage = {
                  id: aiMessageData.message_id,
                  text: messageText,
                  sender: "ai",
                  messageType: "ai",
                  status: "complete",
                  helperText: aiResponseText ? aiMessageData.content : undefined,
                  createdAt: aiMessageData.timestamp ?? new Date().toISOString(),
                };

                if (existingIndex !== -1) {
                  const next = [...filtered];
                  next[existingIndex] = aiMessage;
                  return next;
                }

                // Track seen message ID
                seenMessageIdsRef.current.add(aiMessageData.message_id);
                return [...filtered, aiMessage];
              });
              break;
            }
            case "error": {
              // Map known backend error strings to user-friendly messages
              const sanitizeChatError = (raw: string): string => {
                if (/conversation starter returned no result/i.test(raw) || /resume conversation returned no result/i.test(raw)) {
                  return "I wasn't able to find an answer to that. Try rephrasing your question or ask about a topic related to this section.";
                }
                return sanitizeUpstreamErrorMessage(
                  raw,
                  "AI service temporarily unavailable. Please try again later."
                );
              };

              // Check for authentication failure
              if (handleWebSocketAuthError(data.message, data.type)) {
                return; // Logout is handled
              }

              // Remove thinking message and status indicators on error
              removeThinkingMessage();
              if (threadId) {
                const statusMessageId = `ai-status-${threadId}`;
                updateMessages((prev: TMessage[]) => prev.filter((msg: TMessage) => msg.id !== statusMessageId));
              }

              // If error has user_message_id, associate error with that user message
              const userMessageId = data.details?.user_message_id;
              if (userMessageId) {
                updateMessages((prev) => {
                  const index = prev.findIndex((msg) => msg.id === userMessageId);
                  if (index !== -1) {
                    const next = [...prev];
                    next[index] = {
                      ...next[index],
                      error: sanitizeChatError(data.message),
                      errorCode: data.error_code,
                    };
                    return next;
                  }
                  return prev;
                });
              } else {
                // Fallback: Set error in state if no user_message_id
                setState((prev) => ({
                  ...prev,
                  error: sanitizeChatError(data.message),
                  status: "connected", // Keep connected to allow retry
                }));
              }
              break;
            }
            default:
              break;
          }
        } catch (error: unknown) {
          console.error("[chat] Failed to parse WebSocket message", { error });
        }
      };
    };

    // Initial connection
    connectWebSocket();

    // Cleanup function
    return () => {
      // Mark as intentional close to prevent reconnection
      isIntentionalCloseRef.current = true;

      // Cancel any pending reconnection attempts
      if (reconnectTimeoutRef.current !== null) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      if (thinkingTimeoutRef.current !== null) {
        clearTimeout(thinkingTimeoutRef.current);
        thinkingTimeoutRef.current = null;
      }

      // Close WebSocket connection
      // Set ref to null FIRST so in-flight messages are ignored by the stale WebSocket check
      if (websocketRef.current) {
        const oldWebsocket = websocketRef.current;
        websocketRef.current = null;
        oldWebsocket.close(1000, "Component unmount");
      }
    };
  }, [
    context?.threadId,
    currentUserId,
    addOptimisticMessage,
    appendMessage,
    setErrorState,
    upsertStreamingMessage,
    resolveOptimisticMessage,
    addThinkingMessage,
    removeThinkingMessage,
    updateMessages,
    getCachedThreadMessages,
    setCachedThreadMessages,
    clearThreadInitialPrompt,
  ]);

  const sendMessage = useCallback(
    (content: string, files?: TMessageFile[], options?: TSendMessageOptions) => {
      const trimmed = content.trim();
      if (!trimmed && (!files || files.length === 0)) {
        return;
      }

      if (!context) {
        setErrorState("No chat thread selected.");
        return;
      }

      if (!websocketRef.current || websocketRef.current.readyState !== WebSocket.OPEN) {
        setErrorState("Chat connection is not ready yet.");
        return;
      }

      if (!options?.skipOptimistic) {
        addOptimisticMessage(trimmed || "(No text message)", files);
        // Don't call addThinkingMessage() - let ai_status handler manage thinking status
      }

      // Send message with file references via WebSocket
      const fileIds =
        files && files.length > 0
          ? files.map((f) => f.id).filter((id): id is string => id != null && id !== "")
          : undefined;
      websocketRef.current.send(
        JSON.stringify({
          type: "user_message",
          content: trimmed || "",
          ...(fileIds && fileIds.length > 0 ? { file_ids: fileIds } : {}),
        })
      );
    },
    [addOptimisticMessage, context, setErrorState]
  );

  const retryMessage = useCallback(
    (messageId: string) => {
      const message = state.messages.find((msg) => msg.id === messageId);
      if (message?.sender !== "user" || !message.error) {
        return;
      }

      const { text, files } = message;

      // Remove the failed message so the fresh optimistic takes its place cleanly
      updateMessages((prev) => prev.filter((msg) => msg.id !== messageId));

      // Resend as a fresh message — addOptimisticMessage pushes to the queue so
      // resolveOptimisticMessage can replace it when the backend echoes it back
      sendMessage(text, files);
    },
    [state.messages, sendMessage, updateMessages]
  );

  return {
    status: state.status,
    messages: state.messages,
    error: state.error,
    isReady: state.status === "connected" && !state.messages.some((m) => m.thinking),
    sendMessage,
    retryMessage,
  };
};
